import yup from '~/core/validation';
import { useForm } from 'vee-validate';

// Contact form validation schema; exported for its unit test.
export const contactFormSchema = yup.object({
	email: yup.string().email().optional(),
	name: yup.string().required(),
	phone: yup.string().phone('Deve ser um telefone válido').required(),
	userMessage: yup.string()
});

type ContactFormSchema = yup.InferType<typeof contactFormSchema>;

function hasSameFormValues(
	left: ContactFormSchema,
	right: ContactFormSchema
): boolean {
	return (
		left.name === right.name &&
		left.email === right.email &&
		left.phone === right.phone &&
		left.userMessage === right.userMessage
	);
}

// Contact form composable
export function useContactForm() {
	const { user } = useUserSession();

	// Shared state using Nuxt's useState
	const globalFormState = useState<ContactFormSchema>('contactForm', () => ({
		email: '',
		name: '',
		phone: '',
		userMessage: ''
	}));

	// State to control if form was submitted successfully
	const formSubmittedSuccessfully = useState<boolean>(
		'contactFormSubmittedSuccessfully',
		() => false
	);

	// Function to mark form as submitted successfully
	const setFormSubmittedSuccessfully = (value: boolean) => {
		formSubmittedSuccessfully.value = value;
	};

	const {
		handleSubmit,
		defineField,
		setFieldValue,
		errors,
		resetForm,
		meta,
		validate,
		setValues
	} = useForm<ContactFormSchema>({
		initialValues: globalFormState.value,
		validationSchema: contactFormSchema,
		validateOnMount: false
	});

	// Configure validation triggers per-field: validate on blur/change only, not on input/model updates
	const fieldOptions = {
		validateOnBlur: true,
		validateOnChange: true,
		validateOnInput: false,
		validateOnModelUpdate: false
	} as const;

	const [name, nameAttrs] = defineField('name', fieldOptions);
	const [email, emailAttrs] = defineField('email', fieldOptions);
	const [phone, phoneAttrs] = defineField('phone', fieldOptions);
	const [userMessage, userMessageAttrs] = defineField(
		'userMessage',
		fieldOptions
	);

	// Shared lock across instances to prevent cross-instance ping-pong loops
	const syncingState = useState<boolean>('contactFormSyncing', () => false);

	// Sync form fields with global state (bidirectional)
	watch(
		[name, email, phone, userMessage],
		() => {
			if (syncingState.value) return;

			const nextGlobalState: ContactFormSchema = {
				name: name.value || '',
				email: email.value || '',
				phone: phone.value || '',
				userMessage: userMessage.value || ''
			};

			if (hasSameFormValues(globalFormState.value, nextGlobalState)) return;

			// Do not set syncingState here: nested flush of other instances' global→local
			// watcher can run before unlock and bail out at `if (syncingState.value) return`.
			globalFormState.value = nextGlobalState;
		},
		{ deep: true }
	);

	// Sync global state with form fields (without triggering validation)
	watch(
		globalFormState,
		newGlobalState => {
			if (syncingState.value) return;

			// Update form values only if they are different
			if (
				name.value !== newGlobalState.name ||
				email.value !== newGlobalState.email ||
				phone.value !== newGlobalState.phone ||
				userMessage.value !== newGlobalState.userMessage
			) {
				syncingState.value = true;
				// Avoid validation when syncing from external/global state
				resetForm({
					values: { ...newGlobalState }
				});
				syncingState.value = false;
			}
		},
		{ deep: true }
	);

	// Update fields when user changes (avoid triggering validation on autofill)
	// Only run on client-side to prevent hydration issues
	watch(
		() => user.value,
		newUser => {
			if (!newUser || syncingState.value) return;

			// Check if we should autofill each field (only if field is empty and user has data)
			const shouldFillName = !name.value && newUser.name;
			const shouldFillEmail = !email.value && newUser.email;
			const shouldFillPhone = !phone.value && newUser.phoneNumber;

			// Only proceed if at least one field should be filled
			if (shouldFillName || shouldFillEmail || shouldFillPhone) {
				const updatedValues: ContactFormSchema = {
					name: shouldFillName ? newUser.name || '' : name.value || '',
					email: shouldFillEmail ? newUser.email || '' : email.value || '',
					phone: shouldFillPhone ? getUserFulfilledPhone() : phone.value || '',
					userMessage: userMessage.value || ''
				};

				syncingState.value = true;
				resetForm({ values: { ...updatedValues } });
				syncingState.value = false;
			}
		},
		{ immediate: true }
	);

	// Override setFieldValue to also update global state
	const customSetFieldValue = (
		field: keyof ContactFormSchema,
		value: string
	) => {
		if (syncingState.value) return;

		syncingState.value = true;
		setFieldValue(field, value);
		globalFormState.value = {
			...globalFormState.value,
			[field]: value
		};
		syncingState.value = false;
	};

	// Function to manually autofill from user data
	const autofillFromUser = () => {
		if (!user.value || syncingState.value) return;

		const updatedValues: ContactFormSchema = {
			name: user.value.name ?? name.value ?? '',
			email: user.value.email ?? email.value ?? '',
			phone: getUserFulfilledPhone(),
			userMessage: userMessage.value || ''
		};

		syncingState.value = true;
		resetForm({ values: { ...updatedValues } });
		syncingState.value = false;
	};

	const wrappedHandleSubmit =
		(onSubmit: Parameters<typeof handleSubmit>[0]) =>
		async (e?: Event): Promise<Promise<void>> => {
			const result = await handleSubmit(onSubmit)(e);
			return (result ?? Promise.resolve()) as Promise<void>;
		};

	// Computed to determine if phone should be disabled
	// Uses the `user` ref obtained at the top of useContactForm() for SSR consistency
	const isPhoneDisabled = computed(() => {
		// If no logged user, phone can be edited
		if (!user.value) return false;

		// If user logged in via phone, the phone field should be disabled
		// because they already verified OTP during login
		if (user.value.login_method === 'phone') {
			return true;
		}

		// If user logged in via social (google/facebook) and completed registration,
		// the phone field should be disabled because they already verified OTP
		if (
			(user.value.login_method === 'google' ||
				user.value.login_method === 'facebook') &&
			user.value.hasCompletedRegistration
		) {
			return true;
		}

		// In all other cases, phone can be edited
		return false;
	});

	function getUserFulfilledPhone(): string {
		if (!user.value) return '';

		return user.value.phoneNumber ?? phone.value ?? '';
	}

	return {
		// Form methods
		handleSubmit: wrappedHandleSubmit,
		setFieldValue: customSetFieldValue,
		resetForm,
		validate,
		setFormSubmittedSuccessfully,
		autofillFromUser,
		getUserFulfilledPhone,

		// Form state
		errors,
		meta,
		formSubmittedSuccessfully: readonly(formSubmittedSuccessfully),
		isPhoneDisabled,

		// Form fields (ensuring non-undefined string)
		name: computed(() => name.value || ''),
		email: computed(() => email.value || ''),
		phone: computed(() => phone.value || ''),
		userMessage: computed(() => userMessage.value || ''),

		// Field attributes
		nameAttrs,
		emailAttrs,
		phoneAttrs,
		userMessageAttrs,

		// Schema
		schema: contactFormSchema
	};
}
